import { linkOptions, useLocation } from '@tanstack/react-router';
import { useRef, useState } from 'react';

import { RequestForQuoteViewGatewayMessage } from '../../../types/RequestForQuoteViewGatewayMessage';
import { CenteredPagination } from '../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { GenericError } from '../../common/Error/GenericError';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../common/helpers/formatDate.helper';
import { formatPort } from '../../common/helpers/formatPort.helper';
import { formatVessel } from '../../common/helpers/formatVessel.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { useRole } from '../../common/hooks/useRole';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { OfferStatusTag } from '../../common/OfferStatusTag/OfferStatusTag';
import { Section } from '../../common/Section/Section';
import { RfqVendorReferenceDisplay } from '../../rfq/overview/components/RfqVendorReferenceDisplay';
import { useRfqPagination } from '../../rfq/overview/hooks/useRfqPagination';
import { RecentActivitiesEmptyState } from '../common/components/RecentActivitiesEmptyState';

export const SupplierDashboardRfqTable = () => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const role = useRole();
    const location = useLocation();

    const paginatedRfqsQuery = useRfqPagination({
        page,
        supplierRead: false,
        sortField: 'dateUpdated',
        sortDirection: 'DESC',
    });

    if (paginatedRfqsQuery.isPending) {
        return (
            <Section title={translate('dashboard.recentactivities')}>
                <LoadingIndicator />
            </Section>
        );
    }

    if (paginatedRfqsQuery.isError) {
        return (
            <Section title={translate('dashboard.recentactivities')}>
                <GenericError />
            </Section>
        );
    }

    if (!paginatedRfqsQuery.data?.content.length) {
        return (
            <Section title={translate('dashboard.recentactivities')}>
                <RecentActivitiesEmptyState />
            </Section>
        );
    }

    const buildRowLink = (rfq: RequestForQuoteViewGatewayMessage) => {
        if (rfq.chosenOfferId) {
            return linkOptions({
                to: '/offer/$id',
                params: { id: rfq.chosenOfferId },
                state: { originLocation: location },
            });
        }

        return linkOptions({
            to: '/requests-for-quotes/$id',
            params: { id: rfq.id },
            state: { originLocation: location },
        });
    };

    return (
        <div ref={wrapperRef}>
            <Section title={translate('dashboard.recentactivities')}>
                <TableBuilder<RequestForQuoteViewGatewayMessage>
                    data={paginatedRfqsQuery.data.content}
                    rowLink={buildRowLink}
                    isLoading={paginatedRfqsQuery.isPlaceholderData && paginatedRfqsQuery.isFetching}
                    grow={false}
                >
                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                        header={translate('order.vendorreferenceshort')}
                        width="10%"
                        truncate
                    >
                        {(rfq) => (
                            <RfqVendorReferenceDisplay
                                offerIds={rfq.offerIds}
                                vendorReferences={rfq.vendorReferences}
                                chosenOfferVendorReference={rfq.chosenOfferVendorReference}
                            />
                        )}
                    </TableBuilderColumn>

                    {!role.isOneCompanyUser() ? (
                        <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                            header={translate('offer.company')}
                            width="10%"
                            truncate
                        >
                            {(rfq) =>
                                rfq.supplier ? formatCompanyName({ company: rfq.supplier }) : Characters.EN_DASH
                            }
                        </TableBuilderColumn>
                    ) : null}

                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                        header={translate('offer.customer')}
                        width="15%"
                        truncate
                    >
                        {(rfq) => (rfq.customer ? formatCompanyName({ company: rfq.customer }) : Characters.EN_DASH)}
                    </TableBuilderColumn>

                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                        header={translate('vessel.name')}
                        width="15%"
                        truncate
                    >
                        {(rfq) => (rfq.vessel ? formatVessel({ vessel: rfq.vessel, short: true }) : Characters.EN_DASH)}
                    </TableBuilderColumn>

                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage> header={translate('order.port')} width="20%">
                        {(rfq) => {
                            if (!rfq.port) {
                                return null;
                            }

                            return formatPort({ port: rfq.port, locode: false });
                        }}
                    </TableBuilderColumn>

                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                        header={translate('order.dateDelivery')}
                        width="15%"
                    >
                        {(rfq) => {
                            if (!rfq.dateDelivery) {
                                return null;
                            }
                            return formatDate({ date: rfq.dateDelivery, timeZone: 'UTC' });
                        }}
                    </TableBuilderColumn>

                    <TableBuilderColumn<RequestForQuoteViewGatewayMessage>
                        header={translate('order.status')}
                        width="15%"
                    >
                        {(rfq) => (
                            <OfferStatusTag state={rfq.state} variant="supplier" kycRequested={rfq.kycRequested} />
                        )}
                    </TableBuilderColumn>
                </TableBuilder>

                <CenteredPagination
                    pageTotal={paginatedRfqsQuery.pageTotal}
                    currentPage={page}
                    onPageChange={(page) => {
                        setPage(page);
                        if (wrapperRef.current) {
                            window.scrollTo(0, wrapperRef.current.offsetTop);
                        }
                    }}
                />
            </Section>
        </div>
    );
};
